/* eslint-disable no-underscore-dangle */
import React from 'react';
import { graphql, Link as GatsbyLink, useStaticQuery } from 'gatsby';
import {
  List, Link, Icon, Container, Grid, Heading, Section,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import PhoneNumberWidget from '../../widgets/PhoneNumberWidget';
import { FooterNewsletter } from './FooterNewsletter';

interface WidgetNode {
  node_locale?: string;
  title?: string;
  type?: string;
  children?: {
    title?: string;
    url?: string;
    id?: string;
    path?: string;
    __typename?: string;
  }[];
}

interface WidgetProps {
  type?: string;
  widgets?: {
    edges?: {
      node?: WidgetNode;
    }[];
  };
}

const widgetsQuery = graphql`
  query {
    widgetBusinessHours: allContentfulNavigation(
      filter: {type: {eq: "personalAdvice openingHours"}}
    ) {
      edges {
        node {
          title
          node_locale
          type
          children: contentfulchildren {
            __typename
            ... on Node {
              ... on ContentfulNavItem {
                title
                url
                id
              }
            }
          }
        }
      }
    }
    widgetFAQ: allContentfulNavigation(
      filter: {type: {eq: "personalAdvice FAQ"}}
    ) {
      edges {
        node {
          title
          node_locale
          type
          children: contentfulchildren {
            __typename
            ... on Node {
              ... on ContentfulNavItem {
                title
                url
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const WidgetBusinessHours = ({ widgets, type }: WidgetProps) => {
  const intl = useIntl();
  const edge = widgets?.edges?.find(({ node }) => (
    node?.type === type && node?.node_locale === intl.locale
  ));

  if (!edge?.node) {
    return null;
  }

  const { title, children } = edge.node;

  return (
    <div className="pd-flex pd-gap-2">
      <Icon icon="clock" className="pd-flex-none pd-text-success-500" />
      <div>
        <span className="pd-block pd-font-semibold">{title}</span>
        {children?.filter((item) => item.url || item.path).map((item) => (
          <React.Fragment key={item.url || item.path || item.title}>
            {item.__typename === 'ContentfulNavItem' && (
              <Link href={item.url} variant="secondary">
                {item.title}
              </Link>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export const WidgetFAQ = ({ widgets, type }: WidgetProps) => {
  const intl = useIntl();
  const edge = widgets?.edges?.find(({ node }) => (
    node?.type === type && node?.node_locale === intl.locale
  ));

  if (!edge?.node) {
    return null;
  }

  const { children } = edge.node;

  if (!children || children.length === 0) {
    return null;
  }

  const items = children.filter((item) => item.title && (item.url || item.path));

  return (
    <List variant="icon" icon={<Icon icon="chevron-right" className="pd-text-sm" />}>
      {items.map((item) => (
        <List.Item key={item.url || item.path}>
          {(item.__typename === 'ContentfulPageContainer') && item.path && (
            <Link as={GatsbyLink} to={item.path}>
              {item.title}
            </Link>
          )}
          {item.__typename === 'ContentfulNavItem' && item.url && (
            <Link href={item.url} variant="secondary">
              {item.title}
            </Link>
          )}
        </List.Item>
      ))}
    </List>
  );
};

const PersonalSectionContainer = () => {
  const {
    widgetBusinessHours,
    widgetFAQ,
  } = useStaticQuery(widgetsQuery);

  return (
    <Section variant="dim-branded" className="pd-text-sm">
      <Grid as={Container} className="pd-gap-6 lg:pd-grid-cols-2">
        <Grid.Item>
          <Heading as="div" size="2" className="pd-mb-6">
            <FormattedMessage id="section.customerservice.title" defaultMessage="Customer Service" />
          </Heading>
          <Grid className="pd-gap-6 sm:pd-grid-cols-2">
            <Grid.Item>
              <WidgetFAQ
                widgets={widgetFAQ}
                type="personalAdvice FAQ"
              />
            </Grid.Item>
            <Grid.Item>
              <PhoneNumberWidget
                widgetClasses="pd-mb-6"
                iconClasses="pd-text-success-500"
                componentLocation="footer"
              />
              <WidgetBusinessHours
                widgets={widgetBusinessHours}
                type="personalAdvice openingHours"
              />
            </Grid.Item>
          </Grid>
        </Grid.Item>
        <Grid.Item>
          <FooterNewsletter />
        </Grid.Item>
      </Grid>
    </Section>
  );
};

export default PersonalSectionContainer;
